import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronRight, Copy, Info, Plus, Search } from 'lucide-react';

import { ApiError, request } from '@/api/client';
import type { Club, OrganizationType, Page } from '@/api/types';
import { PageHeader } from '@/components/AppShell';
import { DataTable, PageSizeSelect, Pagination, type Column } from '@/components/DataTable';
import { usePageSize } from '@/lib/pageSize';
import { useToast } from '@/components/Toast';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Modal,
  TableSkeleton,
  TextInput,
  Toggle,
  Select,
} from '@/components/ui';
import { countryFlag, countryName, count, date } from '@/lib/format';
import { useDebounced, useResource } from '@/lib/hooks';
import { useDataScope } from '@/lib/dataPurpose';
import { PurposeBadge, PurposeEditor } from '@/components/DataPurpose';

export function ClubsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { scope } = useDataScope();
  const [purposeEditing, setPurposeEditing] = useState<Club | null>(null);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsRef = useRef(searchParams);
  const locationKeyRef = useRef(location.key);
  if (locationKeyRef.current !== location.key) {
    paramsRef.current = searchParams;
    locationKeyRef.current = location.key;
  }
  const search = Array.from(searchParams.get('search') ?? '').slice(0, 120).join('');
  const organizationTypeValue = searchParams.get('organization_type');
  const organizationType: OrganizationType | '' =
    organizationTypeValue === 'club' || organizationTypeValue === 'nlz' ||
    organizationTypeValue === 'federation' || organizationTypeValue === 'commercial'
      ? organizationTypeValue
      : '';
  const partnerOnly = searchParams.get('partner') === '1' &&
    (organizationType === '' || organizationType === 'club');
  const pageValue = Number(searchParams.get('page'));
  const page = Number.isSafeInteger(pageValue) && pageValue > 0 ? pageValue : 1;
  const sortValue = searchParams.get('sort');
  const sort = sortValue === 'country' || sortValue === 'is_partner' ? sortValue : 'name';
  const order = searchParams.get('order') === 'desc' ? 'desc' : 'asc';
  const [pending, setPending] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const debouncedSearch = useDebounced(search);
  const [pageSize, setPageSize] = usePageSize('clubs');
  const returnTo = `${location.pathname}${location.search}`;
  const detailUrl = (clubId: string, tab?: 'teams') => {
    const next = new URLSearchParams({ from: returnTo });
    if (tab) next.set('tab', tab);
    return `/clubs/${encodeURIComponent(clubId)}?${next.toString()}`;
  };

  function updateFilters(changes: Record<string, string>, resetPage = true) {
    const next = new URLSearchParams(paramsRef.current);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (resetPage) next.delete('page');
    paramsRef.current = next;
    setSearchParams(next, { replace: true });
  }

  const { data, error, loading, reload } = useResource<Page<Club>>(
    '/api/v1/admin/clubs',
    {
      search: debouncedSearch,
      partner_only: partnerOnly || undefined,
      organization_type: organizationType || undefined,
      sort,
      order,
      page,
      page_size: pageSize,
      data_purpose: scope,
    },
    [debouncedSearch, partnerOnly, organizationType, sort, order, page, pageSize, scope],
  );

  useEffect(() => {
    if (search !== debouncedSearch || loading || error || !data || data.page !== page) return;
    const lastPage = Math.max(1, Math.ceil(data.total / pageSize));
    if (page > lastPage) updateFilters({ page: lastPage === 1 ? '' : String(lastPage) }, false);
    // The optimistic URL ref keeps this correction in step with adjacent actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, debouncedSearch, error, loading, page, pageSize, search]);

  const language = i18n.language;
  const filtered = search.trim() !== '' || partnerOnly || organizationType !== '';

  async function copyLink(club: Club) {
    if (club.organization_type === 'commercial') return;
    try {
      await navigator.clipboard.writeText(club.invite_url);
      toast.success(t('clubs.linkCopied'));
    } catch {
      // Clipboard is refused outside a secure context; the link is on screen
      // and can be selected by hand, so this is not worth an error.
    }
  }

  async function setPartner(club: Club, isPartner: boolean) {
    if (club.organization_type !== 'club') return;
    setPending(club.id);
    try {
      await request(`/api/v1/admin/clubs/${club.id}/partner`, {
        method: 'PATCH',
        body: { is_partner: isPartner },
      });
      toast.success(
        t(isPartner ? 'clubs.partnerOn' : 'clubs.partnerOff', { name: club.name }),
      );
      reload();
    } catch {
      toast.failure(t('common.loadFailed'));
    } finally {
      setPending(null);
    }
  }

  const columns: Column<Club>[] = [
    {
      key: 'name',
      header: t('clubs.name'),
      sortKey: 'name',
      render: (club) => (
        <div className="min-w-0">
          <Link to={detailUrl(club.id)} className="block truncate font-medium text-ink hover:text-brand hover:underline">{club.name}</Link>
          {club.organization_type !== 'commercial' ? (
            <div className="truncate text-2xs text-ink-subtle">{club.club_code}</div>
          ) : null}
          <div className="mt-1"><PurposeBadge purpose={club.data_purpose} /></div>
          <button type="button" className="mt-1 text-2xs text-brand hover:underline" onClick={() => setPurposeEditing(club)}>{t('dataPurpose.edit')}</button>
        </div>
      ),
    },
    {
      key: 'organizationType',
      header: t('clubs.organizationType'),
      render: (club) => t(`clubs.organizationTypes.${club.organization_type}`),
    },
    {
      key: 'country',
      header: t('clubs.country'),
      sortKey: 'country',
      render: (club) =>
        club.country ? (
          <span className="text-ink-muted">
            <span aria-hidden>{countryFlag(club.country)} </span>
            {countryName(club.country, language)}
          </span>
        ) : (
          <span aria-hidden className="text-ink-subtle">
            —
          </span>
        ),
    },
    {
      // Commercial organizations invite coaches through their teams; their
      // club code cannot be used as a staff invitation.
      key: 'inviteUrl',
      header: t('clubs.trainerLink'),
      render: (club) => club.organization_type === 'commercial' ? (
        <span className="text-xs text-ink-subtle">{t('clubs.commercialCoachInvite')}</span>
      ) : (
        <div className="flex items-center gap-2">
          <code className="truncate rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-2xs text-ink-muted">
            /v/{club.club_code}
          </code>
          <IconButton
            variant="ghost"
            label={t('clubs.copyLink')}
            icon={<Copy className="h-3.5 w-3.5" />}
            onClick={() => copyLink(club)}
          />
        </div>
      ),
    },
    {
      key: 'athletes',
      header: t('clubs.athletes'),
      sortKey: undefined,
      align: 'right',
      render: (club) => count(club.athlete_count, language),
    },
    {
      key: 'teams',
      header: t('clubs.teams'),
      align: 'right',
      render: (club) => <Link to={detailUrl(club.id, 'teams')} className="text-brand hover:underline">{count(club.team_count, language)}</Link>,
    },
    {
      key: 'coaches',
      header: t('clubs.coaches'),
      align: 'right',
      render: (club) => count(club.coach_count, language),
    },
    {
      key: 'partnerSince',
      header: t('clubs.partnerSince'),
      align: 'right',
      render: (club) =>
        club.partner_since ? (
          <span className="text-ink-subtle">{date(club.partner_since, language)}</span>
        ) : (
          <span aria-hidden className="text-ink-subtle">
            —
          </span>
        ),
    },
    {
      key: 'details',
      header: t('clubs.details'),
      align: 'right',
      render: (club) => <Link to={detailUrl(club.id)} className="inline-flex min-h-9 items-center gap-1 rounded px-2 text-brand hover:bg-surface-sunken hover:underline" aria-label={t('clubs.viewDetailsFor', { name: club.name })}>{t('clubs.details')}<ChevronRight aria-hidden className="h-4 w-4" /></Link>,
    },
    {
      key: 'partner',
      header: t('clubs.partner'),
      sortKey: 'is_partner',
      align: 'right',
      render: (club) => (
        <div className="flex items-center justify-end gap-2">
          {club.organization_type !== 'club' ? (
            <span className="text-ink-subtle">—</span>
          ) : (
            <>
              {club.is_partner ? <Badge tone="positive">{t('clubs.partner')}</Badge> : null}
              <Toggle
                checked={club.is_partner}
                disabled={pending === club.id}
                onChange={(next) => setPartner(club, next)}
                label={t(club.is_partner ? 'clubs.removePartner' : 'clubs.makePartner')}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('clubs.title')}
        subtitle={t('clubs.subtitle')}
        actions={
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setCreating(true)}
          >
            {t('clubs.create')}
          </Button>
        }
      />

      <div className="card">
        <div className="flex flex-wrap items-center gap-3 border-b border-line p-3">
          <div className="relative min-w-[16rem] flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
            />
            <TextInput
              type="search"
              maxLength={120}
              aria-label={t('common.search')}
              placeholder={t('clubs.name')}
              className="pl-9"
              value={search}
              onChange={(event) => {
                updateFilters({ search: event.target.value });
              }}
            />
          </div>
          <Select
            aria-label={t('clubs.organizationType')}
            value={organizationType}
            onChange={(event) => updateFilters({ organization_type: event.target.value, partner: '' })}
          >
            <option value="">{t('clubs.allOrganizationTypes')}</option>
            <option value="club">{t('clubs.organizationTypes.club')}</option>
            <option value="nlz">{t('clubs.organizationTypes.nlz')}</option>
            <option value="federation">{t('clubs.organizationTypes.federation')}</option>
            <option value="commercial">{t('clubs.organizationTypes.commercial')}</option>
          </Select>
          <label className="flex min-h-[2.25rem] items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-line text-brand"
              checked={partnerOnly}
              disabled={organizationType !== '' && organizationType !== 'club'}
              onChange={(event) => {
                updateFilters({ partner: event.target.checked ? '1' : '' });
              }}
            />
            {t('clubs.partnerOnly')}
          </label>
          <PageSizeSelect
            value={pageSize}
            onChange={(next) => {
              setPageSize(next);
              updateFilters({ page: '' }, false);
            }}
          />
        </div>

        {error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={reload} />
        ) : search !== debouncedSearch || (loading && !data) ? (
          <TableSkeleton columns={9} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-8 w-8" />}
            title={t(filtered ? 'clubs.emptyTitle' : 'clubs.noClubsTitle')}
            body={t(filtered ? 'clubs.emptyBody' : 'clubs.noClubsBody')}
            action={
              filtered ? <Button onClick={() => updateFilters({ search: '', partner: '', organization_type: '' })}>
                {t('common.reset')}
              </Button> :
              <Button variant="primary" onClick={() => setCreating(true)}>
                {t('clubs.create')}
              </Button>
            }
          />
        ) : (
          <>
            <DataTable
              caption={t('clubs.title')}
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(club) => club.id}
              sort={sort}
              order={order}
              onSort={(key) => {
                updateFilters({ sort: key === 'name' ? '' : key, order: sort === key && order === 'asc' ? 'desc' : '' });
              }}
            />
            <Pagination
              page={page}
              pageSize={pageSize}
              total={data?.total ?? 0}
              onPage={(next) => updateFilters({ page: next > 1 ? String(next) : '' }, false)}
            />
          </>
        )}
      </div>

      <p className="mt-3 flex items-start gap-1.5 text-xs text-ink-subtle">
        <Info aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {t('clubs.partnerExplainer')}
      </p>

      {creating ? (
        <CreateClub
          onClose={() => setCreating(false)}
          onCreated={(club) => {
            setCreating(false);
            toast.success(t('clubs.created', { name: club.name }));
            reload();
          }}
        />
      ) : null}
      {purposeEditing ? <PurposeEditor entityType="clubs" entityId={purposeEditing.id}
        name={purposeEditing.name} purpose={purposeEditing.data_purpose}
        onClose={() => setPurposeEditing(null)} onSaved={() => { setPurposeEditing(null); reload(); }} /> : null}
    </>
  );
}

function CreateClub({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (club: Club) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [isPartner, setIsPartner] = useState(false);
  const [busy, setBusy] = useState(false);

  const trimmed = name.trim();
  // Two letters or nothing: the column is an ISO 3166-1 alpha-2 code.
  const countryValid = country === '' || /^[A-Za-z]{2}$/.test(country);
  const valid = trimmed.length > 0 && trimmed.length <= 100 && countryValid;

  async function create() {
    setBusy(true);
    try {
      const club = await request<Club>('/api/v1/admin/clubs', {
        method: 'POST',
        body: {
          name: trimmed,
          country: country ? country.toUpperCase() : null,
          is_partner: isPartner,
        },
      });
      onCreated(club);
    } catch (error) {
      // A name already in use is the one failure worth naming: it means the
      // club is already there and its link already exists.
      const conflict = error instanceof ApiError && error.status === 409;
      toast.failure(conflict ? t('clubs.duplicateName') : t('common.loadFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('clubs.create')}
      description={t('clubs.createHint')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={busy} disabled={!valid} onClick={create}>
            {busy ? t('clubs.creating') : t('clubs.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('clubs.name')}>
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              maxLength={100}
              placeholder={t('clubs.namePlaceholder')}
              value={name}
              invalid={name.length > 0 && !trimmed}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('clubs.country')} hint={t('clubs.countryHint')}>
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              maxLength={2}
              placeholder="DE"
              value={country}
              invalid={!countryValid}
              onChange={(event) => setCountry(event.target.value)}
            />
          )}
        </Field>
        <label className="flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-line text-brand"
            checked={isPartner}
            onChange={(event) => setIsPartner(event.target.checked)}
          />
          <span>
            {t('clubs.makePartner')}
            <span className="mt-0.5 block text-xs text-ink-subtle">
              {t('clubs.partnerOnCreateHint')}
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
